import { GlassCard } from "@/components/ui/glass-card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AppWindow } from "lucide-react";
import { type Device } from "@/hooks/useDevices";

interface InstalledApplicationsProps {
  device: Device;
}

const InstalledApplications = ({ device }: InstalledApplicationsProps) => {
  console.log('InstalledApplications received props:', { device });

  const getAppName = (app: any): string => {
    if (!app.name) return 'Unknown App';
    return app.name.replace(/\.app$/, '');
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  const getIconSrc = (icon: string | undefined): string | null => {
    if (!icon) return null;
    
    // If already a data URI or URL, return as-is
    if (icon.startsWith('data:') || icon.startsWith('http')) {
      return icon;
    }
    
    // Convert raw base64 to data URI
    return `data:image/png;base64,${icon}`;
  };

  // Get apps from both installed_apps array and the device's installed_apps JSON field
  const deviceApps = Array.isArray(device.installed_apps) ? device.installed_apps : [];
  const hasApps = deviceApps.length > 0;

  console.log('Processing apps array:', { 
    hasApps, 
    appsCount: deviceApps.length, 
    firstApp: deviceApps[0] 
  });

  // Sort apps by name and version
  const sortedApps = [...deviceApps].sort((a, b) => {
    const nameCompare = (a.name || '').localeCompare(b.name || '');
    if (nameCompare !== 0) return nameCompare;
    return ((a.version || '') || '').localeCompare((b.version || '') || '');
  });

  return (
    <GlassCard variant="default" hover={false} className="p-6">
      <h2 className="text-xl font-semibold mb-4 text-slate-900 dark:text-white">
        Installed Applications ({hasApps ? deviceApps.length : 0})
      </h2>
      
      {hasApps ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-left">Application</TableHead>
              <TableHead className="text-left">Version</TableHead>
              <TableHead className="text-left">Installation Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedApps.map((app, index) => (
              <TableRow key={`${app.name}-${app.version}-${index}`}>
                <TableCell className="flex items-center text-left">
                  {app.icon && getIconSrc(app.icon) ? (
                    <img 
                      src={getIconSrc(app.icon) || ''} 
                      alt={`${getAppName(app)} icon`}
                      className="h-8 w-8 rounded mr-3 flex-shrink-0 object-contain"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const fallback = document.createElement('div');
                        fallback.className = 'h-8 w-8 mr-3 flex items-center justify-center flex-shrink-0';
                        fallback.innerHTML = '<svg class="h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"/><path d="M9 8h7"/><path d="M9 12h7"/><path d="M9 16h7"/></svg>';
                        target.parentNode?.insertBefore(fallback, target);
                      }}
                    />
                  ) : (
                    <div className="h-8 w-8 mr-3 flex items-center justify-center flex-shrink-0">
                      <AppWindow className="h-5 w-5 text-blue-600" />
                    </div>
                  )}
                  <span className="font-medium">{getAppName(app)}</span>
                </TableCell>
                <TableCell className="text-left">{app.version || 'N/A'}</TableCell>
                <TableCell className="text-left">{formatDate(app.installed_date)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="text-center py-8 text-slate-500">
          <AppWindow className="h-12 w-12 mx-auto mb-3 text-slate-400" />
          <p className="text-lg font-medium mb-2">No Applications Found</p>
          <p className="text-sm text-slate-400">
            This device hasn't reported any installed applications yet. 
            Applications will appear here once they are detected during the next device check-in.
          </p>
        </div>
      )}
    </GlassCard>
  );
};

export default InstalledApplications;